'use client';

import { useState } from 'react';
import { BookOpen, User, Calendar, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Memory } from '@/lib/types';
import { formatDate, getInitials } from '@/lib/utils';

interface MemoryWallProps {
  memories: Memory[];
  memorialSlug?: string;
}

export function MemoryWall({ memories, memorialSlug }: MemoryWallProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    authorName: '',
    relationship: '',
    title: '',
    content: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasMemories = memories && memories.length > 0;

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className="memorial-section">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        {hasMemories ? (
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary mb-4">
              Erinnerungen & Geschichten
            </h2>
            <p className="text-memorial-text-secondary">
              {memories.length} {memories.length === 1 ? 'Erinnerung wurde geteilt' : 'Erinnerungen wurden geteilt'}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-memorial-muted flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-memorial-secondary" />
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary mb-4">
              Erinnerungen
            </h2>
            <p className="text-memorial-text-secondary mb-6">
              Teilen Sie Ihre Erinnerungen und Geschichten
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-memorial-primary text-white rounded-full hover:bg-memorial-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Erinnerung teilen</span>
            </button>
          </div>
        )}

        {/* Memory list */}
        {hasMemories && (
        <>
        <div className="space-y-6">
          {memories.map((memory) => {
            const isExpanded = expandedId === memory.id;
            const shouldTruncate = memory.content.length > 300;
            const displayContent = shouldTruncate && !isExpanded
              ? memory.content.substring(0, 300) + '...'
              : memory.content;

            return (
              <article
                key={memory.id}
                className="bg-white rounded-2xl p-6 shadow-sm border border-memorial-border"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-memorial-secondary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-memorial-secondary font-medium">
                      {getInitials(memory.authorName.split(' ')[0], memory.authorName.split(' ')[1] || '')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-memorial-text-primary">
                        {memory.authorName}
                      </span>
                      {memory.relationship && (
                        <span className="text-sm text-memorial-text-secondary">
                          • {memory.relationship}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-memorial-text-muted mt-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(memory.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Title */}
                {memory.title && (
                  <h3 className="font-serif text-xl text-memorial-primary mb-3">
                    &ldquo;{memory.title}&rdquo;
                  </h3>
                )}

                {/* Content */}
                <div className="prose prose-sm max-w-none text-memorial-text-primary">
                  <p className="whitespace-pre-line">{displayContent}</p>
                </div>

                {/* Expand/Collapse Button */}
                {shouldTruncate && (
                  <button
                    onClick={() => toggleExpand(memory.id)}
                    className="flex items-center gap-1 mt-3 text-sm text-memorial-secondary hover:text-memorial-primary transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <span>Weniger anzeigen</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span>Weiterlesen</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </article>
            );
          })}
        </div>

        {/* Add Memory Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-memorial-primary text-white rounded-full hover:bg-memorial-primary/90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Eigene Erinnerung teilen</span>
          </button>
        </div>
        </>
        )}

        {/* Memory Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-serif text-xl text-memorial-primary mb-2">
                    Vielen Dank für Ihre Erinnerung
                  </h3>
                  <p className="text-memorial-text-secondary mb-6">
                    Ihre Erinnerung wurde übermittelt und wird nach Prüfung veröffentlicht.
                  </p>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setSubmitted(false);
                      setFormData({ authorName: '', relationship: '', title: '', content: '' });
                    }}
                    className="px-6 py-3 rounded-full bg-memorial-primary text-white hover:bg-memorial-primary/90 transition-colors"
                  >
                    Schließen
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-memorial-primary mb-4">
                    Erinnerung teilen
                  </h3>
                  <p className="text-memorial-text-secondary mb-6">
                    Teilen Sie eine persönliche Erinnerung oder Geschichte.
                  </p>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                      {error}
                    </div>
                  )}

                  <form className="space-y-4" onSubmit={async (e) => {
                    e.preventDefault();
                    if (!memorialSlug || !formData.authorName.trim() || !formData.content.trim()) return;

                    setIsSubmitting(true);
                    setError(null);

                    try {
                      const response = await fetch(`/api/memorials/${memorialSlug}/memories`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          authorName: formData.authorName.trim(),
                          relationship: formData.relationship.trim() || null,
                          title: formData.title.trim() || null,
                          content: formData.content.trim(),
                        }),
                      });

                      const data = await response.json();

                      if (!response.ok) {
                        throw new Error(data.error || 'Failed to submit memory');
                      }

                      setSubmitted(true);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}>
                    <div>
                      <label className="block text-sm font-medium text-memorial-text-primary mb-1">
                        Ihr Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.authorName}
                        onChange={(e) => setFormData(prev => ({ ...prev, authorName: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                        placeholder="Max Mustermann"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-memorial-text-primary mb-1">
                        Beziehung (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.relationship}
                        onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                        placeholder="z.B. Kollege, Nachbar, Freund"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-memorial-text-primary mb-1">
                        Titel (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
                        placeholder="z.B. Ein unvergesslicher Moment"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-memorial-text-primary mb-1">
                        Ihre Erinnerung *
                      </label>
                      <textarea
                        required
                        rows={5}
                        value={formData.content}
                        onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10 resize-none"
                        placeholder="Teilen Sie Ihre Geschichte..."
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setError(null);
                          setFormData({ authorName: '', relationship: '', title: '', content: '' });
                        }}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-3 rounded-full border border-memorial-border text-memorial-text-primary hover:bg-memorial-muted transition-colors disabled:opacity-50"
                      >
                        Abbrechen
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || !formData.authorName.trim() || !formData.content.trim()}
                        className="flex-1 px-4 py-3 rounded-full bg-memorial-primary text-white hover:bg-memorial-primary/90 transition-colors disabled:opacity-50"
                      >
                        {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
